/*
 * Strategy review access (Cortex).
 *
 * A reader without a Cortex login reviews a strategy by proving a contact.
 * They request a code (POST /api/media-campaigns/:campaignId/strategy-review-
 * access/request with the email or mobile they typed). A contact that already
 * has access gets it at once; anyone else's request goes to a Cphere
 * superadmin, and the code arrives if they approve. Contact + code
 * (.../verify) then set this browser's review grant: an HttpOnly cookie this
 * code never sees, remembered for 30 days unless the reader says otherwise.
 * The strategy-microsite read of THAT campaign, made with credentials, then
 * returns the same CLIENT_REVIEW body a signed-in client reviewer receives.
 *
 * Hard rules:
 *   - the contact, the code and the grant never appear in a URL, a log, an
 *     analytics event or an error message;
 *   - nothing here ever says whether a contact is known, approved or
 *     pending: a request is answered the same way either way, and only its
 *     HTTP status is read to tell outcomes apart;
 *   - the grant is never readable here: the backend sets, sends and clears
 *     its cookie; this page only asks for reads with credentials, on the API
 *     origin the cookie belongs to;
 *   - a pending request lives in localStorage under
 *     cphere:strategy-review-request:<numericCampaignId> for at most fifteen
 *     minutes, and is dropped on success or when abandoned.
 *
 * Every storage access is guarded: storage can be unavailable or refused
 * outright, and nothing here may break the page that asks.
 */

export const STRATEGY_REVIEW_GRANT_STATUS_HEADER = "X-Cphere-Strategy-Review-Grant-Status";
export const STRATEGY_REVIEW_CONTACT_MAX_LENGTH = 254;
// Six characters, with room for the spaces or dashes a reader may add.
export const STRATEGY_REVIEW_CODE_MAX_LENGTH = 12;

const STRATEGY_REVIEW_REQUEST_STORAGE_PREFIX = "cphere:strategy-review-request:";

// A pending request is kept no longer than a code lives, and never longer
// than fifteen minutes.
export const STRATEGY_REVIEW_REQUEST_LIFETIME_SECONDS = 15 * 60;
const DEFAULT_RESEND_AFTER_SECONDS = 60;
const REQUEST_CLOCK_SKEW_MS = 60 * 1000;

export const STRATEGY_REVIEW_ACCESS_OUTCOME = Object.freeze({
  REQUESTED: "requested",
  GRANTED: "granted",
  INVALID_CONTACT: "invalid_contact",
  INVALID_CODE: "invalid_code",
  LOCKED: "locked",
  UNAVAILABLE: "unavailable",
  ABORTED: "aborted",
});

// `requested` is the panel's own sentence, shown for every 2xx whatever the
// backend did with the contact; the response's `message` is never displayed.
export const STRATEGY_REVIEW_ACCESS_MESSAGES = Object.freeze({
  requested:
    "If access is available, a verification code has been sent. If approval is required, you'll receive a code once access is approved.",
  invalidContact: "Enter an email address or mobile number.",
  invalidCode: "That code isn't valid or has expired.",
  unavailable: "Access codes aren't available right now. Please try again later.",
});

/* The backend's own rule for a public campaign id: digits, a safe integer, > 0. */
export const normalizeStrategyReviewCampaignId = (value) => {
  const raw = String(value ?? "").trim();
  if (!/^\d+$/.test(raw)) return "";
  const id = Number(raw);
  return Number.isSafeInteger(id) && id > 0 ? String(id) : "";
};

const storageKeyFor = (prefix, campaignId) => {
  const id = normalizeStrategyReviewCampaignId(campaignId);
  return id ? `${prefix}${id}` : "";
};

export const strategyReviewRequestStorageKey = (campaignId) =>
  storageKeyFor(STRATEGY_REVIEW_REQUEST_STORAGE_PREFIX, campaignId);

// ---------------------------------------------------------------------------
// The grant: an HttpOnly cookie on the API origin
// ---------------------------------------------------------------------------

const CPHERE_SITE = "cphere.ai";
const CPHERE_SAME_SITE_API = "https://api.cphere.ai";
// The production backend, under its Render name and under the site's own.
const CPHERE_PRODUCTION_APIS = new Set(["https://cphere-backend.onrender.com", CPHERE_SAME_SITE_API]);

/*
 * Where the review endpoints and the review read go: an origin on the same
 * site as this page, so the browser keeps the grant cookie the verify sets
 * and sends it back (a cookie from another site is refused outright by
 * Safari). REACT_APP_STRATEGY_REVIEW_API_URL decides when it is set; a
 * cphere.ai page built for the production backend uses api.cphere.ai, the
 * same backend under the site's own name; any other build (a staging backend
 * behind a cphere.ai name, local development, previews) keeps its own backend.
 */
export const resolveStrategyReviewApiBase = (
  fallbackBase = "",
  env = process.env,
  location = typeof window !== "undefined" ? window.location : null
) => {
  const configured = String(env?.REACT_APP_STRATEGY_REVIEW_API_URL || "").trim();
  if (/^https?:\/\/[^/]+/i.test(configured)) return configured.replace(/\/+$/, "");
  const fallback = String(fallbackBase || "").trim().replace(/\/+$/, "");
  const host = String(location?.hostname || "").toLowerCase();
  const onCphereSite = host === CPHERE_SITE || host.endsWith(`.${CPHERE_SITE}`);
  return onCphereSite && CPHERE_PRODUCTION_APIS.has(fallback.toLowerCase()) ? CPHERE_SAME_SITE_API : fallback;
};

/*
 * The backend's verdict on this browser's review cookie:
 *   "accepted"    — the grant is live; the body is the CLIENT_REVIEW body;
 *   "refused"     — the grant is dead (the backend has already cleared it);
 *   "unavailable" — the backend could not check it (storage not ready, or a
 *                   transient error); the body is the public one;
 *   ""            — no verdict (no cookie, or a value this build does not know).
 */
const STRATEGY_REVIEW_GRANT_VERDICTS = new Set(["accepted", "refused", "unavailable"]);

export const readStrategyReviewGrantStatus = (response) => {
  try {
    const status = String(response?.headers?.get?.(STRATEGY_REVIEW_GRANT_STATUS_HEADER) || "")
      .trim()
      .toLowerCase();
    return STRATEGY_REVIEW_GRANT_VERDICTS.has(status) ? status : "";
  } catch {
    return "";
  }
};

/**
 * The strategy-microsite read. For a reader without a Cortex session
 * (`withReviewCookie`) it is made with credentials, so this browser's review
 * cookie for the campaign rides along if it holds one; a signed-in reader's
 * read carries none. Resolves `{ response, grantStatus }`.
 *
 * The cookie must never be able to break a read that works without it: when
 * the credentialed read fails in transport, the plain public read is made
 * instead — from `fallbackUrl` when given (the default backend) — and the
 * page loads exactly as it would have without one.
 */
export const fetchStrategyMicrositeWithReviewGrant = async (url, init = {}, { withReviewCookie = false, fallbackUrl = "" } = {}) => {
  if (!withReviewCookie) return { response: await fetch(url, init), grantStatus: "" };
  let response;
  try {
    response = await fetch(url, { ...init, credentials: "include" });
  } catch (error) {
    if (error?.name === "AbortError") throw error;
    return { response: await fetch(fallbackUrl || url, { ...init, credentials: "omit" }), grantStatus: "" };
  }
  return { response, grantStatus: readStrategyReviewGrantStatus(response) };
};

// ---------------------------------------------------------------------------
// The pending request
// ---------------------------------------------------------------------------

const usableStrategyReviewContact = (value) => {
  const contact = typeof value === "string" ? value.trim() : "";
  return contact && contact.length <= STRATEGY_REVIEW_CONTACT_MAX_LENGTH ? contact : "";
};

/*
 * A stored request is data this browser wrote, but storage is not trusted: a
 * record that is malformed, expired, dated in the future, or promised to live
 * longer than any request does, is no request at all.
 */
const validPendingRequest = (value, now) => {
  if (!value || typeof value !== "object") return null;
  const contact = usableStrategyReviewContact(value.contact);
  const requestedAt = Number(value.requestedAt);
  const resendAfterSeconds = Number(value.resendAfterSeconds);
  const expiresAt = Number(value.expiresAt);
  if (!contact) return null;
  if (![requestedAt, resendAfterSeconds, expiresAt].every(Number.isFinite)) return null;
  if (requestedAt > now + REQUEST_CLOCK_SKEW_MS) return null;
  if (expiresAt <= now) return null;
  if (expiresAt - requestedAt > STRATEGY_REVIEW_REQUEST_LIFETIME_SECONDS * 1000) return null;
  if (resendAfterSeconds < 0 || resendAfterSeconds > STRATEGY_REVIEW_REQUEST_LIFETIME_SECONDS) {
    return null;
  }
  return { contact, requestedAt, resendAfterSeconds, expiresAt };
};

const parsePendingRequest = (raw, now) => {
  if (typeof raw !== "string" || !raw) return null;
  try {
    return validPendingRequest(JSON.parse(raw), now);
  } catch {
    return null;
  }
};

export const forgetStrategyReviewRequest = (campaignId) => {
  const key = strategyReviewRequestStorageKey(campaignId);
  if (!key) return;
  try {
    window.localStorage.removeItem(key);
  } catch {
    // Storage is unavailable, so nothing was kept there to forget.
  }
};

/* The pending request for this campaign, or null. An unusable one is dropped. */
export const readStrategyReviewRequest = (campaignId, now = Date.now()) => {
  const key = strategyReviewRequestStorageKey(campaignId);
  if (!key) return null;
  let raw;
  try {
    raw = window.localStorage.getItem(key);
  } catch {
    return null;
  }
  if (raw === null || raw === undefined) return null;
  const pending = parsePendingRequest(raw, now);
  if (!pending) forgetStrategyReviewRequest(campaignId);
  return pending;
};

export const storeStrategyReviewRequest = (campaignId, pending, now = Date.now()) => {
  const key = strategyReviewRequestStorageKey(campaignId);
  const valid = validPendingRequest(pending, now);
  if (!key || !valid) return false;
  try {
    window.localStorage.setItem(key, JSON.stringify(valid));
    return true;
  } catch {
    return false;
  }
};

/* Drop every stored request — for any campaign — that has outlived its code. */
export const sweepExpiredStrategyReviewRequests = (now = Date.now()) => {
  try {
    const storage = window.localStorage;
    const keys = [];
    for (let index = 0; index < storage.length; index += 1) {
      const key = storage.key(index);
      if (key && key.startsWith(STRATEGY_REVIEW_REQUEST_STORAGE_PREFIX)) keys.push(key);
    }
    keys.forEach((key) => {
      if (!parsePendingRequest(storage.getItem(key), now)) storage.removeItem(key);
    });
  } catch {
    // Storage is unavailable, so nothing was kept there to sweep.
  }
};

// ---------------------------------------------------------------------------
// The two public calls
// ---------------------------------------------------------------------------

const readJsonBody = async (response) => {
  try {
    return await response.json();
  } catch {
    return null;
  }
};

const resolveRetryAfterSeconds = (body, response) => {
  const fromBody = Number(body?.retryAfterSeconds);
  if (Number.isFinite(fromBody) && fromBody > 0) return fromBody;
  try {
    const fromHeader = Number(response?.headers?.get?.("Retry-After"));
    return Number.isFinite(fromHeader) && fromHeader > 0 ? fromHeader : null;
  } catch {
    return null;
  }
};

const boundedSeconds = (value, fallback, { min = 0, max }) => {
  const seconds = Number(value);
  if (value === null || value === undefined || !Number.isFinite(seconds) || seconds < min) {
    return fallback;
  }
  return Math.min(seconds, max);
};

const accessEndpoint = (apiBaseUrl, id, action) =>
  `${String(apiBaseUrl || "").trim().replace(/\/+$/, "")}/api/media-campaigns/${encodeURIComponent(
    id
  )}/strategy-review-access/${action}`;

const postPublicJson = (url, body, signal, { credentials = "same-origin" } = {}) =>
  fetch(url, {
    method: "POST",
    // A public endpoint: what the reader typed travels in the body, and no
    // Authorization header is ever sent with it.
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    cache: "no-store",
    credentials,
    signal,
  });

const transportFailure = (error) => ({
  outcome:
    error?.name === "AbortError"
      ? STRATEGY_REVIEW_ACCESS_OUTCOME.ABORTED
      : STRATEGY_REVIEW_ACCESS_OUTCOME.UNAVAILABLE,
});

/**
 * Ask for a code to be sent to `contact`.
 *
 * The backend answers every contact identically — one with access, one whose
 * request now waits for a superadmin, one it will never admit — so the
 * outcome is decided by the HTTP status alone: any 2xx is REQUESTED — never
 * "sent" — and is kept as this campaign's pending request (best effort: a
 * browser without storage still carries on in memory). The body is read only
 * for its cooldown and code lifetime, which do not depend on the contact.
 *
 * Resolves `{ outcome, pending?, retryAfterSeconds? }`.
 */
export const requestStrategyReviewAccessCode = async ({
  apiBaseUrl,
  campaignId,
  contact,
  signal,
} = {}) => {
  const id = normalizeStrategyReviewCampaignId(campaignId);
  const submitted = usableStrategyReviewContact(contact);
  if (!id || !String(apiBaseUrl || "").trim()) {
    return { outcome: STRATEGY_REVIEW_ACCESS_OUTCOME.UNAVAILABLE };
  }
  if (!submitted) return { outcome: STRATEGY_REVIEW_ACCESS_OUTCOME.INVALID_CONTACT };

  let response;
  try {
    response = await postPublicJson(
      accessEndpoint(apiBaseUrl, id, "request"),
      { contact: submitted },
      signal
    );
  } catch (error) {
    return transportFailure(error);
  }

  const body = await readJsonBody(response);
  if (response?.ok === true) {
    const requestedAt = Date.now();
    const pending = {
      contact: submitted,
      requestedAt,
      resendAfterSeconds: boundedSeconds(body?.resendAfterSeconds, DEFAULT_RESEND_AFTER_SECONDS, {
        max: STRATEGY_REVIEW_REQUEST_LIFETIME_SECONDS,
      }),
      expiresAt:
        requestedAt +
        boundedSeconds(body?.codeExpiresInSeconds, STRATEGY_REVIEW_REQUEST_LIFETIME_SECONDS, {
          min: 1,
          max: STRATEGY_REVIEW_REQUEST_LIFETIME_SECONDS,
        }) *
          1000,
    };
    storeStrategyReviewRequest(id, pending, requestedAt);
    return { outcome: STRATEGY_REVIEW_ACCESS_OUTCOME.REQUESTED, pending };
  }
  if (response?.status === 400) return { outcome: STRATEGY_REVIEW_ACCESS_OUTCOME.INVALID_CONTACT };
  if (response?.status === 429) {
    return {
      outcome: STRATEGY_REVIEW_ACCESS_OUTCOME.LOCKED,
      retryAfterSeconds: resolveRetryAfterSeconds(body, response),
    };
  }
  return { outcome: STRATEGY_REVIEW_ACCESS_OUTCOME.UNAVAILABLE };
};

/**
 * Exchange contact + code for this browser's review grant.
 *
 * The verify is made with credentials so the browser keeps the HttpOnly
 * cookie it sets — for 30 days when `remember` (the default), otherwise for
 * the browser session. The grant is never in the body and never here.
 * Resolves `{ outcome, remembered? }`, plus `retryAfterSeconds` when locked;
 * on success the pending request is dropped.
 */
export const verifyStrategyReviewAccessCode = async ({
  apiBaseUrl,
  campaignId,
  contact,
  code,
  remember = true,
  signal,
} = {}) => {
  const id = normalizeStrategyReviewCampaignId(campaignId);
  const submittedContact = usableStrategyReviewContact(contact);
  const submittedCode = String(code ?? "").trim();
  if (!id || !String(apiBaseUrl || "").trim()) {
    return { outcome: STRATEGY_REVIEW_ACCESS_OUTCOME.UNAVAILABLE };
  }
  if (!submittedContact || !submittedCode) {
    return { outcome: STRATEGY_REVIEW_ACCESS_OUTCOME.INVALID_CODE };
  }

  let response;
  try {
    response = await postPublicJson(
      accessEndpoint(apiBaseUrl, id, "verify"),
      { contact: submittedContact, code: submittedCode, remember: remember !== false },
      signal,
      { credentials: "include" }
    );
  } catch (error) {
    return transportFailure(error);
  }

  const body = await readJsonBody(response);
  if (response?.ok === true && body?.ok === true) {
    forgetStrategyReviewRequest(id);
    return { outcome: STRATEGY_REVIEW_ACCESS_OUTCOME.GRANTED, remembered: body.remembered === true };
  }
  if (response?.status === 401) return { outcome: STRATEGY_REVIEW_ACCESS_OUTCOME.INVALID_CODE };
  if (response?.status === 429) {
    return {
      outcome: STRATEGY_REVIEW_ACCESS_OUTCOME.LOCKED,
      retryAfterSeconds: resolveRetryAfterSeconds(body, response),
    };
  }
  return { outcome: STRATEGY_REVIEW_ACCESS_OUTCOME.UNAVAILABLE };
};

// ---------------------------------------------------------------------------
// What the reader is told
// ---------------------------------------------------------------------------

/* Whole minutes, rounded up, never fewer than one. */
export const strategyReviewLockoutMinutes = (retryAfterSeconds) => {
  const seconds = Number(retryAfterSeconds);
  if (!Number.isFinite(seconds) || seconds <= 0) return 1;
  return Math.max(1, Math.ceil(seconds / 60));
};

/* The sentence for a failed request or check. It never repeats what was typed. */
export const describeStrategyReviewAccessFailure = (result) => {
  switch (result?.outcome) {
    case STRATEGY_REVIEW_ACCESS_OUTCOME.INVALID_CONTACT:
      return STRATEGY_REVIEW_ACCESS_MESSAGES.invalidContact;
    case STRATEGY_REVIEW_ACCESS_OUTCOME.INVALID_CODE:
      return STRATEGY_REVIEW_ACCESS_MESSAGES.invalidCode;
    case STRATEGY_REVIEW_ACCESS_OUTCOME.LOCKED: {
      const minutes = strategyReviewLockoutMinutes(result.retryAfterSeconds);
      return `Too many attempts. Try again in ${minutes} ${minutes === 1 ? "minute" : "minutes"}.`;
    }
    default:
      return STRATEGY_REVIEW_ACCESS_MESSAGES.unavailable;
  }
};
